import api from './client'

/**
 * Torrent (BT / Metalink) library entry — mirrors the wire shape returned by
 * the backend `GET /api/torrents` and `afd.torrentList` JSON-RPC.
 *
 * `createdAt` is a convenience alias for `addedAt` (file mtime on disk) so UI
 * code can sort by a single canonical name regardless of source.
 */
export interface TorrentInfo {
  /** 40-char lowercase hex SHA-1 of the torrent metainfo. */
  infoHash: string
  /** Display name parsed from the metainfo `name` field (may be empty). */
  name: string
  /** Total content size in bytes. */
  size: number
  /** Bytes downloaded so far (per-task or cumulative — backend depends on source). */
  downloaded: number
  /** Bytes uploaded so far. */
  uploaded: number
  /** Upload / Download ratio. 0 when nothing has been downloaded yet. */
  ratio: number
  /** Combined seeders + leechers count. Backend exposes only `peers`; seeders/leechers
   *  are derived client-side from the running AFD snapshot where possible. */
  peers: number
  /** Best-effort seeder count (≥0). Derived from AFD task if available, else 0. */
  seeders: number
  /** Best-effort leecher count (≥0). Derived from AFD task if available, else 0. */
  leechers: number
  /** AFD status string (`active` / `waiting` / `paused` / `complete` / `error` / `removed`). */
  status: string
  /** Local filesystem path of the .torrent file (relative to data dir). Always non-empty. */
  savePath: string
  /** Unix-seconds when the torrent was added to the library (file mtime). */
  createdAt: number
  /** Task GID of the running task, if currently loaded into the service. */
  gid?: string
}

/**
 * Raw entry as returned by `GET /api/torrents` (qBittorrent-style fields).
 * We keep this internal so we can normalize + enrich without polluting callers.
 */
interface RawTorrentEntry {
  infoHash?: string
  info_hash?: string
  name?: string
  size?: number
  addedAt?: number
  added_at?: number
  downloaded?: number
  uploaded?: number
  ratio?: number
  peers?: number
  status?: string
  gid?: string
  savePath?: string
  save_path?: string
}

interface ListTorrentsResponse {
  torrents?: RawTorrentEntry[]
  total?: number
}

/** Normalize a raw entry into our canonical {@link TorrentInfo} shape. */
function normalize(entry: RawTorrentEntry): TorrentInfo {
  const infoHash = (entry.infoHash ?? entry.info_hash ?? '').toLowerCase()
  const ratioRaw = typeof entry.ratio === 'number' ? entry.ratio : Number(entry.ratio ?? 0)
  const peersRaw = typeof entry.peers === 'number' ? entry.peers : Number(entry.peers ?? 0)
  const sizeRaw = typeof entry.size === 'number' ? entry.size : Number(entry.size ?? 0)
  const downloadedRaw = typeof entry.downloaded === 'number'
    ? entry.downloaded
    : Number(entry.downloaded ?? 0)
  const uploadedRaw = typeof entry.uploaded === 'number'
    ? entry.uploaded
    : Number(entry.uploaded ?? 0)
  const addedAtRaw = typeof entry.addedAt === 'number'
    ? entry.addedAt
    : Number(entry.added_at ?? 0)

  // Backend `peers` is a combined count (seeders + leechers). We expose both
  // fields for UI clarity; without an active AFD snapshot we conservatively
  // attribute all peers to leechers (matches qBittorrent UI when no peers yet).
  const peers = Number.isFinite(peersRaw) && peersRaw >= 0 ? Math.floor(peersRaw) : 0
  const seeders = 0
  const leechers = peers

  return {
    infoHash,
    name: entry.name ?? '',
    size: Number.isFinite(sizeRaw) ? sizeRaw : 0,
    downloaded: Number.isFinite(downloadedRaw) ? downloadedRaw : 0,
    uploaded: Number.isFinite(uploadedRaw) ? uploadedRaw : 0,
    ratio: Number.isFinite(ratioRaw) ? ratioRaw : 0,
    peers,
    seeders,
    leechers,
    status: entry.status ?? 'unknown',
    savePath: entry.savePath ?? entry.save_path ?? '',
    createdAt: Number.isFinite(addedAtRaw) ? Math.floor(addedAtRaw) : 0,
    gid: entry.gid || undefined,
  }
}

/**
 * Fetch the full BT/Metalink torrent library from the backend.
 *
 * Endpoint: `GET /api/torrents` — returns `{ torrents: [...], total: N }`.
 * Thin wrapper that flattens the envelope and normalizes entries.
 */
export async function listTorrents(): Promise<TorrentInfo[]> {
  const resp = await api.get<ListTorrentsResponse>('/api/torrents')
  const raw = Array.isArray(resp?.torrents) ? resp.torrents : []
  return raw.map(normalize)
}

/**
 * Remove a torrent by info hash (40 lowercase hex chars).
 *
 * Endpoint: `POST /api/torrents/{hash}/remove` — calls `afd.torrentRemove`
 * under the hood, which removes the library index and deletes the
 * underlying .torrent file. Running AFD tasks associated with the
 * torrent are not automatically removed (caller can pair with `removeTask`).
 */
export async function removeTorrent(hash: string): Promise<void> {
  const trimmed = (hash ?? '').trim().toLowerCase()
  if (!trimmed) {
    throw new Error('info hash is required')
  }
  if (trimmed.length !== 40 || !/^[0-9a-f]{40}$/.test(trimmed)) {
    throw new Error('info hash must be 40 lowercase hex chars')
  }
  return api.post<void>(`/api/torrents/${trimmed}/remove`, {})
}
